import { logger } from '@springtree/eva-sdk-core-logger';
import { orderBy } from 'lodash-es';
import { v4 } from 'uuid';
/**
 * The basic interceptor implementation
 *
 * @export
 * @class Interceptor
 */
export class Interceptor {
    name;
    /**
     * Creates an instance of an Interceptor
     * Extending classes will need to set their name and interceptor type
     * and implement their interceptor handler
     *
     * @param {string} name
     * @param {('request'|'response')} type
     */
    constructor(name) {
        this.name = name;
    }
    /**
     * Retrieves the name of the interceptor
     *
     * @return {string}
     */
    getName() {
        return this.name;
    }
    /**
     * The interceptor token as registered with the registry;
     *
     * @private
     * @type {string}
     */
    registryToken;
    /**
     * Used to influence the order interceptors are processed in
     * Higher priority hooks are processed first.
     *
     */
    priority = 1;
    /**
     * Stores an activation token as provided by the service registry
     *
     */
    set token(newToken) {
        if (newToken !== undefined && this.registryToken) {
            throw new Error(`${this.name} interceptor is already active`);
        }
        this.registryToken = newToken;
    }
    /**
     * Retrieves the current token
     *
     * @returns
     */
    get token() {
        return this.registryToken;
    }
    /**
     * Check if the token provided is the activation token for this interceptor
     *
     * @param {string} token
     * @returns
     */
    hasToken(token) {
        return this.registryToken === token;
    }
    /**
     * Used to disable an interceptor
     *
     */
    disabled = false;
    /**
     * Indicates the interceptor is active
     *
     * @readonly
     */
    get isActive() {
        return !!this.registryToken;
    }
    /**
     * The request interceptor implementation
     *
     * @type {IRequestInterceptor}
     */
    requestInterceptor;
    /**
     * The response interceptor implementation
     *
     * @type {IResponseInterceptor}
     */
    responseInterceptor;
    /**
     * Registers and enables the interceptor
     *
     */
    activate() {
        if (this.token) {
            throw new Error(`${this.name} interceptor is already active`);
        }
        evaServiceInterceptors.addInterceptor(this);
    }
    /**
     * Disables the interceptor and stops warning logging
     *
     */
    deactivate() {
        if (!this.token) {
            throw new Error(`${this.name} interceptor is not active`);
        }
        evaServiceInterceptors.removeInterceptor(this.token);
        this.registryToken = undefined;
        logger.debug(`[${this.name}] Disabled processing`)();
    }
}
/**
 * This class manages the interceptors that need to be added to the EVA Service calls
 *
 * @export
 * @class InterceptorRepository
 */
export class InterceptorRepository {
    /**
     * The collection of request interceptors
     *
     * @private
     * @type {Interceptor[]}
     */
    interceptors = [];
    /**
     * Returns the currently active interceptors
     * Ordered by priority
     *
     * @returns
     */
    getActiveInterceptors() {
        return orderBy(this.interceptors.filter(interceptor => interceptor.isActive && !interceptor.disabled), 'priority', 'desc');
    }
    /**
     * Return the list of active request interceptors ordered by priority
     *
     * @returns
     */
    getRequestInterceptors() {
        const activeInterceptors = this.getActiveInterceptors();
        const activeRequestInterceptors = activeInterceptors.filter(interceptor => !!interceptor.requestInterceptor);
        const hooks = activeRequestInterceptors.map(interceptor => interceptor.requestInterceptor.hook);
        return hooks;
    }
    /**
     * Return the list of active request interceptors ordered by priority
     *
     * @returns
     */
    getResponseInterceptors() {
        const activeInterceptors = this.getActiveInterceptors();
        const activeResponseInterceptors = activeInterceptors.filter(interceptor => !!interceptor.responseInterceptor);
        const hooks = activeResponseInterceptors.map(interceptor => interceptor.responseInterceptor.hook);
        return hooks;
    }
    /**
     * Register and activate a new interceptor
     *
     * @param {Interceptor} interceptor
     * @returns {string}
     */
    addInterceptor(interceptor) {
        // This will throw an error if the interceptor has already been activated
        //
        const token = v4();
        interceptor.token = token;
        this.interceptors.push(interceptor);
        logger.log(`Added service interceptor: ${interceptor.getName()}. Active interceptors: ${this.interceptors.length}`)();
        return token;
    }
    /**
     * Remove an interceptor from the registry
     *
     * @param {string} token
     */
    removeInterceptor(token) {
        const interceptor = this.interceptors.find(interceptor => interceptor.hasToken(token));
        if (interceptor) {
            this.interceptors = this.interceptors.filter(interceptor => !interceptor.hasToken(token));
            logger.log(`Removed service interceptor: ${interceptor.getName()}. Active interceptors: ${this.interceptors.length}`)();
        }
    }
}
export const evaServiceInterceptors = new InterceptorRepository();
//# sourceMappingURL=interceptors.js.map